import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { ChevronLeft, ChevronRight, ArrowUpRight, Zap } from 'lucide-react';
import services1 from '../../assets/home/services/services1.png';
import services2 from '../../assets/home/services/services2.png';
import services3 from '../../assets/home/services/services3.png';
import services4 from '../../assets/home/services/services4.png';
import services5 from '../../assets/home/services/services5.png';
import services6 from '../../assets/home/services/services6.png';
import services7 from '../../assets/home/services/services7.png';
import services8 from '../../assets/home/services/services8.png';

const ServicesSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoScrolling, setIsAutoScrolling] = useState(true);
  const [servicesPerSlide, setServicesPerSlide] = useState(3);
  const [isUserInteracting, setIsUserInteracting] = useState(false);
  const [hoveredService, setHoveredService] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const sectionRef = useRef(null);
  const autoScrollRef = useRef(null);
  const resumeTimeoutRef = useRef(null);

  // Services data with enhanced content
  const services = useMemo(() => [
    {
      img: services1,
      title: "Logistic Marketplace",
      description: "AI-powered platform connecting shippers and carriers with intelligent matching algorithms.",
      category: "Platform",
      alt: "Logistics marketplace platform connecting shippers with verified carriers",
      color: "#3B82F6"
    },
    {
      img: services2,
      title: "Vehicle Aggregation",
      description: "Smart fleet unification for maximum efficiency and resource optimization.",
      category: "Fleet",
      alt: "Vehicle aggregation service for unified fleet management",
      color: "#10B981"
    },
    {
      img: services3,
      title: "Reverse Logistics",
      description: "Streamlined returns management with predictive analytics and automation.",
      category: "Returns",
      alt: "Reverse logistics solutions for efficient return management",
      color: "#8B5CF6"
    },
    {
      img: services4,
      title: "First Mile Delivery",
      description: "Precision pickup orchestration from origin to distribution networks.",
      category: "Pickup",
      alt: "First mile delivery services for pickup and initial transport",
      color: "#F59E0B"
    },
    {
      img: services5,
      title: "Mid-Mile Transport",
      description: "Optimized hub-to-hub freight movement with real-time route intelligence.",
      category: "Transport",
      alt: "Mid-mile transport optimization between distribution centers",
      color: "#EF4444"
    },
    {
      img: services6,
      title: "Per-Unit Pricing",
      description: "Dynamic pricing models that adapt to your business requirements and volume.",
      category: "Pricing",
      alt: "Flexible per-unit pricing model for cost-effective shipping",
      color: "#06B6D4"
    },
    {
      img: services7,
      title: "Last-Mile Delivery",
      description: "Hyper-local delivery networks ensuring customer satisfaction at every doorstep.",
      category: "Delivery",
      alt: "Last-mile delivery services for final destination shipping",
      color: "#84CC16"
    },
    {
      img: services8,
      title: "Managed Services",
      description: "End-to-end logistics orchestration with dedicated account management.",
      category: "Management",
      alt: "Managed logistics services for complete supply chain solutions",
      color: "#F97316"
    }
  ], []);

  // Mouse tracking
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth) * 2 - 1,
        y: (e.clientY / window.innerHeight) * 2 - 1
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Calculate responsive services per slide
  const getServicesPerSlide = useCallback(() => {
    if (typeof window === 'undefined') return 3;
    const width = window.innerWidth;
    if (width < 768) return 1;   // Mobile
    if (width < 1024) return 2;  // Tablet
    return 3;                    // Desktop
  }, []);

  const totalSlides = useMemo(() =>
    Math.ceil(services.length / servicesPerSlide),
    [services.length, servicesPerSlide]
  );

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const newServicesPerSlide = getServicesPerSlide();
      setServicesPerSlide(newServicesPerSlide);
      const newTotalSlides = Math.ceil(services.length / newServicesPerSlide);
      setCurrentSlide(prev => Math.min(prev, newTotalSlides - 1));
    };

    setServicesPerSlide(getServicesPerSlide());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [getServicesPerSlide, services.length]);

  // Intersection Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Auto-scroll functionality
  useEffect(() => {
    if (autoScrollRef.current) {
      clearInterval(autoScrollRef.current);
    }

    if (isAutoScrolling && isVisible && !isUserInteracting && totalSlides > 1) {
      autoScrollRef.current = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % totalSlides);
      }, 4000);
    }

    return () => {
      if (autoScrollRef.current) {
        clearInterval(autoScrollRef.current);
      }
    };
  }, [isAutoScrolling, isVisible, isUserInteracting, totalSlides]);

  const handleUserInteraction = useCallback(() => {
    setIsUserInteracting(true);
    setIsAutoScrolling(false);

    if (resumeTimeoutRef.current) {
      clearTimeout(resumeTimeoutRef.current);
    }

    resumeTimeoutRef.current = setTimeout(() => {
      setIsUserInteracting(false);
      setIsAutoScrolling(true);
    }, 8000);
  }, []);

  const nextSlide = useCallback(() => {
    handleUserInteraction();
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
  }, [handleUserInteraction, totalSlides]);

  const prevSlide = useCallback(() => {
    handleUserInteraction();
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
  }, [handleUserInteraction, totalSlides]);

  const goToSlide = useCallback((slideIndex) => {
    if (slideIndex === currentSlide) return;
    handleUserInteraction();
    setCurrentSlide(slideIndex);
  }, [currentSlide, handleUserInteraction]);

  return (
    <section
      ref={sectionRef}
      className="w-full bg-gray-100 relative overflow-hidden"
      aria-labelledby="services-section-title"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative">

        {/* Header Section */}
        <div className="mb-20">
          <div className="grid lg:grid-cols-12 gap-12 items-end">

            {/* Left Content */}
            <div className="lg:col-span-7">
              <div
                className={`transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                  }`}
              >
                <div className="flex items-center mb-6">
                  <div className="w-12 h-px bg-[#F05A28] mr-4"></div>
                  <span className="text-[#F05A28] font-semibold tracking-wider text-sm uppercase flex items-center">
                    <Zap className="w-4 h-4 mr-2" />
                    Our Services
                  </span>
                </div>

                <h2
                  id="services-section-title"
                  className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight"
                >
                  Services crafted for
                  <br />
                  <span className="text-[#F05A28] italic">modern logistics</span>
                </h2>
              </div>
            </div>
          </div>
        </div>

        {/* Services Carousel */}
        <div
          className={`relative transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'
            }`}
          style={{ transitionDelay: '0.4s' }}
        >
          {/* Navigation */}
          {totalSlides > 1 && (
            <div className="flex justify-between items-center mb-8">
              <div className="flex space-x-3">
                <button
                  onClick={prevSlide}
                  className="w-12 h-12 rounded-full border-2 border-gray-200 hover:border-[#F05A28] flex items-center justify-center transition-all duration-300 hover:bg-[#F05A28] hover:text-white group"
                  aria-label="Previous services"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={nextSlide}
                  className="w-12 h-12 rounded-full border-2 border-gray-200 hover:border-[#F05A28] flex items-center justify-center transition-all duration-300 hover:bg-[#F05A28] hover:text-white group"
                  aria-label="Next services"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}

          {/* Services Grid */}
          <div className="relative overflow-hidden pb-2">
            <div
              className="flex transition-transform duration-700 ease-out"
              style={{
                transform: `translateX(-${currentSlide * (100 / totalSlides)}%)`,
                width: `${totalSlides * 100}%`
              }}
            >
              {Array.from({ length: totalSlides }).map((_, slideIndex) => (
                <div
                  key={`slide-${slideIndex}`}
                  className="w-full flex-shrink-0"
                  style={{ width: `${100 / totalSlides}%` }}
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 px-2">
                    {services.slice(slideIndex * servicesPerSlide, (slideIndex + 1) * servicesPerSlide).map((service, index) => {
                      const globalIndex = slideIndex * servicesPerSlide + index;
                      const isHovered = hoveredService === globalIndex;

                      return (
                        <div
                          key={`service-${globalIndex}`}
                          className={`group relative bg-white rounded-3xl p-8 transition-all duration-500 cursor-pointer border border-gray-100 hover:border-gray-200 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-16 opacity-0'
                            }`}
                          style={{
                            transitionDelay: `${0.6 + index * 0.1}s`,
                            boxShadow: isHovered ? '0 25px 50px -12px rgba(0, 0, 0, 0.15)' : '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
                          }}
                          onMouseEnter={() => setHoveredService(globalIndex)}
                          onMouseLeave={() => setHoveredService(null)}
                        >
                          {/* Category Badge */}
                          <div className="absolute top-6 right-6">
                            <span
                              className="px-3 py-1 rounded-full text-xs font-medium border transition-all duration-300"
                              style={{
                                backgroundColor: `${service.color}15`,
                                borderColor: isHovered ? `${service.color}30` : '#E5E7EB',
                                color: service.color
                              }}
                            >
                              {service.category}
                            </span>
                          </div>

                          {/* Service Icon */}
                          <div className="mb-8">
                            <div
                              className="w-20 h-20 rounded-2xl flex items-center justify-center transition-all duration-500 group-hover:scale-110"
                              style={{
                                backgroundColor: `${service.color}15`
                              }}
                            >
                              <img
                                src={service.img}
                                alt={service.alt}
                                className="w-12 h-12 object-contain transition-transform duration-300"
                                loading={slideIndex === 0 ? "eager" : "lazy"}
                              />
                            </div>
                          </div>

                          {/* Content */}
                          <div className="space-y-4">
                            <h3 className="text-2xl font-bold text-gray-900 group-hover:text-gray-800 transition-colors duration-300">
                              {service.title}
                            </h3>

                            <p className="text-gray-600 leading-relaxed">
                              {service.description}
                            </p>
                          </div>

                          {/* Accent Line */}
                          <div
                            className="absolute bottom-0 left-8 right-8 h-1 rounded-full transition-all duration-500 origin-left"
                            style={{
                              backgroundColor: service.color,
                              transform: isHovered ? 'scaleX(1)' : 'scaleX(0)'
                            }}
                          ></div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;